import React, {
  FC,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import ArrowClockIcon from 'icons/ArrowClock.icon';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import {
  DATA_ROOM_NODE_VERSION_TYPE,
  DataRoomNode,
  DataRoomNodeVersion,
} from 'router/subrouters/DataRoom/models/DataRoomNode';
import { AvatarWithInitials } from 'components/AvatarWithInitials';
import { getFormattedDate, DATE_FORMAT_SHORT_SLASH_WITH_TIME } from 'dateUtils';
import DataroomNodeViewModal from 'components/DataroomNodeViewModal';
import { CollapsibleSection } from 'components/CollapsibleSection';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';

import './DataRoomDocumentHistory.styles.scss';

const isTypeRename = (type: string) =>
  type === DATA_ROOM_NODE_VERSION_TYPE.FILE_RENAME;

type DataRoomDocumentHistoryProps = {
  className?: string;
  node: DataRoomNode;
  canFetch: boolean;
};

const DataRoomDocumentHistory: FC<DataRoomDocumentHistoryProps> = ({
  className,
  node,
  canFetch,
}) => {
  const [history, setHistory] = useState<DataRoomNodeVersion[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedNode, setSelectedNode] = useState<
    DataRoomNodeVersion & { linkToFile: string | null }
  >(null);

  const previewModal = useRef<OverlayRef>(null);

  const {
    dataRoom: { userId, name },
    qaSide,
  } = useContext(DataRoomContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const versionId = selectedNode?.linkToFile || selectedNode?._id;

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  useEffect(() => {
    (async () => {
      if (canFetch) {
        try {
          setIsLoading(true);
          const {
            data: { items },
          } = await dataRoomApi.nodes.getFileHistory(node?._id, {
            $limit: '100',
          });
          setHistory(items);
        } catch (e) {
          mixpanelService.errorViewed(
            getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
          );
        } finally {
          setIsLoading(false);
        }
      }
    })();
  }, [canFetch, node?._id, popServerError, setIsLoading, getMixpanelProps]);

  const historyWithLinkToFile = useMemo(
    () =>
      history?.map((el, index) => ({
        ...el,
        linkToFile: isTypeRename(el.type)
          ? history.find((el, i) => i > index && !isTypeRename(el.type))?._id ||
            undefined
          : el._id,
      })),
    [history],
  );

  if (!history?.length || isLoading) return null;

  return (
    <div className="white-panel">
      <CollapsibleSection
        className={classNames('dr-document-history', className)}
        trigger={
          <div className="trigger">
            <ArrowClockIcon />
            {t('fileVersionHistory')}
          </div>
        }
      >
        {historyWithLinkToFile.map((el) => {
          const {
            _id,
            type,
            version,
            previousName,
            user,
            updatedAt,
            linkToFile,
          } = el;

          const isRename = isTypeRename(type);

          return (
            <div
              key={_id}
              className={classNames('dr-document-history__version', {
                'file-link': !!linkToFile,
              })}
              {...(!!linkToFile && {
                onClick: () => {
                  setSelectedNode(el);
                  previewModal?.current?.open();
                },
              })}
            >
              <span className="dr-document-history__version-type">
                {isRename ? (
                  <>
                    <p>{t('renamedFrom')}</p>
                    {previousName}
                  </>
                ) : (
                  t('versionNumber', { version })
                )}
              </span>

              <span className="dr-document-history__version-date">
                {getFormattedDate(updatedAt, DATE_FORMAT_SHORT_SLASH_WITH_TIME)}
              </span>

              <AvatarWithInitials user={user} />
            </div>
          );
        })}
      </CollapsibleSection>

      <DataroomNodeViewModal
        ref={previewModal}
        isCurrentlyActiveNode={false}
        ownerId={userId}
        userSide={qaSide}
        dataRoomName={name}
        node={{
          ...node,
          ...selectedNode,
          _id: versionId,
          canDownload: false,
        }}
      />
    </div>
  );
};

export default DataRoomDocumentHistory;
